import type {
  MemberAccountUpdateRequest,
  MemberChangePasswordRequest,
  MemberLoginRequest,
  MemberRegisterRequest,
} from "@features/authentication/types";

import { useAuthStore } from "@features/authentication/stores";
import { computed } from "vue";
import { useRouter } from "vue-router";

export function useAuth() {
  // Stores
  const router = useRouter();
  const authStore = useAuthStore();

  // Computed properties from the store
  const user = computed(() => authStore.currentUser);
  const authenticated = computed(() => authStore.isAuthenticated);
  const loading = computed(() => authStore.loading);
  const error = computed(() => authStore.error);

  /**
   * Login
   */
  const login = async (credentials: MemberLoginRequest, rememberMe: boolean) => {
    return await authStore.login(credentials, rememberMe);
  };

  /**
   * Register
   */
  const register = async (credentials: MemberRegisterRequest) => {
    return await authStore.register(credentials);
  };

  /**
   * Update account details
   */
  const updateAccount = async (data: MemberAccountUpdateRequest) => {
    return await authStore.updateAccountDetails(data);
  };

  /**
   * Change user password
   */
  const changePassword = async (data: MemberChangePasswordRequest) => {
    return await authStore.changePassword(data);
  };

  /**
   * Logout authenticated user
   */
  const logout = () => {
    try {
      authStore.logout();
      router.replace({ path: "/my-account" }); // Redirect to my-account after logout
    } catch (error) {
      console.error("[Auth Composables] Logout failed:", error);
    }
  };

  /**
   * Clear any error messages
   */
  const clearError = () => authStore.clearError();

  // Methods
  const methods = {
    changePassword,
    clearError,
    login,
    logout,
    register,
    updateAccount,
  };

  // States
  const states = {
    authenticated,
    error,
    loading,
    user,
  };

  return {
    ...methods,
    ...states,
  };
}
